import * as XLSX from 'xlsx';

import { analyzePotfUpc } from './analyzePotfUpc';
import { analyzeRdStockWithoutItem } from './analyzeRdStockWithoutItem';
import { analyzeSupersededWithStock } from './analyzeSupersededWithStock';
import { streamXlsbRows } from '@/lib/xlsx/streamXlsbRows';
import {
  analyzeSuspendedItem,
  SuspendedItemScan,
  type SuspendedItemAnalysis,
} from './analyzeSuspendedItem';
import { analyzeVendorItemList } from './analyzeVendorItemList';
import { analyzeWebNoPrice } from './analyzeWebNoPrice';
import {
  DETECT_HEAD_ROWS,
  detectDailyReport,
  detectDailyReportFromHeads,
} from './detectDailyReport';
import { isXlsbFile } from './excelFile';
import { readFileArrayBuffer } from './readFileArrayBuffer';
import type { RdStockAnalysisBuckets } from './rdStockAnalysis';
import type { DailyReportFileBody, DailyReportFileResult } from './types';

/**
 * Classify a parsed workbook. ITEMSUSPE4, STOCK_V, STOCKS_V, WEBNOPRF*,
 * POTF_UPC*, and VITMLIST* are analyzed; other known kinds stay `recognized`.
 */
export function analyzeDailyReportWorkbook(
  workbook: XLSX.WorkBook,
  fileName: string,
  id: string = crypto.randomUUID(),
): DailyReportFileResult {
  const kind = detectDailyReport(workbook, fileName);
  if (kind === 'unknown') {
    return { status: 'unrecognized', kind, fileName, id };
  }
  if (kind === 'suspendedItem') {
    return { ...analyzedSuspended(workbook, fileName), id };
  }
  if (kind === 'rdStockWithoutItem') {
    return {
      ...analyzedMappedKind(
        workbook,
        fileName,
        'rdStockWithoutItem',
        analyzeRdStockWithoutItem,
      ),
      id,
    };
  }
  if (kind === 'supersededWithStock') {
    return {
      ...analyzedMappedKind(
        workbook,
        fileName,
        'supersededWithStock',
        analyzeSupersededWithStock,
      ),
      id,
    };
  }
  if (kind === 'webNoPrice') {
    return { ...analyzedWebNoPrice(workbook, fileName), id };
  }
  if (kind === 'potfUpc') {
    return { ...analyzedPotfUpcFile(workbook, fileName), id };
  }
  if (kind === 'vendorItemList') {
    return { ...analyzedVendorItemListFile(workbook, fileName), id };
  }
  return { status: 'recognized', kind, fileName, id };
}

function analyzedSuspended(
  workbook: XLSX.WorkBook,
  fileName: string,
): DailyReportFileBody {
  return suspendedBody(analyzeSuspendedItem(workbook, fileName), fileName);
}

function suspendedBody(
  analysis: SuspendedItemAnalysis | null,
  fileName: string,
): DailyReportFileBody {
  if (!analysis) {
    return { status: 'unreadable', fileName };
  }
  return {
    status: 'analyzed',
    kind: 'suspendedItem',
    fileName,
    rowsScanned: analysis.rowsScanned,
    flagged: analysis.flagged,
  };
}

function analyzedMappedKind(
  workbook: XLSX.WorkBook,
  fileName: string,
  kind: 'rdStockWithoutItem' | 'supersededWithStock',
  analyze: (
    workbook: XLSX.WorkBook,
    fileName: string,
  ) => RdStockAnalysisBuckets | null,
): DailyReportFileBody {
  const analysis = analyze(workbook, fileName);
  if (!analysis) {
    return { status: 'unreadable', fileName };
  }
  return {
    status: 'analyzed',
    kind,
    fileName,
    rowsScanned: analysis.rowsScanned,
    mapped: analysis.mapped,
    flagged: analysis.flagged,
  };
}

function analyzedWebNoPrice(
  workbook: XLSX.WorkBook,
  fileName: string,
): DailyReportFileBody {
  const analysis = analyzeWebNoPrice(workbook, fileName);
  if (!analysis) {
    return { status: 'unreadable', fileName };
  }
  return {
    status: 'analyzed',
    kind: 'webNoPrice',
    fileName,
    rowsScanned: analysis.rowsScanned,
    flagged: analysis.flagged,
  };
}

function analyzedPotfUpcFile(
  workbook: XLSX.WorkBook,
  fileName: string,
): DailyReportFileBody {
  const analysis = analyzePotfUpc(workbook, fileName);
  if (!analysis) {
    return { status: 'unreadable', fileName };
  }
  return {
    status: 'analyzed',
    kind: 'potfUpc',
    fileName,
    rowsScanned: analysis.rowsScanned,
    flagged: analysis.flagged,
  };
}

function analyzedVendorItemListFile(
  workbook: XLSX.WorkBook,
  fileName: string,
): DailyReportFileBody {
  const analysis = analyzeVendorItemList(workbook, fileName);
  if (!analysis) {
    return { status: 'unreadable', fileName };
  }
  return {
    status: 'analyzed',
    kind: 'vendorItemList',
    fileName,
    rowsScanned: analysis.rowsScanned,
    flagged: analysis.flagged,
  };
}

/** The first {@link DETECT_HEAD_ROWS} rows of every sheet. */
async function readXlsbHeads(file: File): Promise<unknown[][][]> {
  const heads: unknown[][][] = [];
  await streamXlsbRows(file, () => {
    const rows: unknown[][] = [];
    heads.push(rows);
    return (row) => {
      rows.push(row);
      return rows.length < DETECT_HEAD_ROWS;
    };
  });
  return heads;
}

async function streamSuspendedItem(
  file: File,
): Promise<SuspendedItemAnalysis | null> {
  const scan = new SuspendedItemScan(file.name);
  await streamXlsbRows(file, () => {
    const take = scan.sheet();
    return (row, rowIndex) => {
      take(row, rowIndex);
      return true;
    };
  });
  return scan.result();
}

/**
 * An `.xlsb` re-save. ITEMSUSPE4 runs to millions of rows, more than a
 * whole-workbook parse survives, so it is analyzed as its sheets stream by.
 * The other reports are small and take the in-memory path.
 */
async function analyzeXlsbFile(
  file: File,
  id: string,
): Promise<DailyReportFileResult> {
  const kind = detectDailyReportFromHeads(await readXlsbHeads(file), file.name);
  // The workbook path detects from the same head rows and filename, so a
  // whole-workbook parse (which a huge file may never finish) can't change it.
  if (kind === 'unknown') {
    return { status: 'unrecognized', kind, fileName: file.name, id };
  }
  if (kind === 'suspendedItem') {
    return {
      ...suspendedBody(await streamSuspendedItem(file), file.name),
      id,
    };
  }
  const workbook = XLSX.read(await readFileArrayBuffer(file), {
    type: 'array',
  });
  return analyzeDailyReportWorkbook(workbook, file.name, id);
}

/** Read a dropped Excel file and run {@link analyzeDailyReportWorkbook}. */
export async function analyzeDailyReportFile(
  file: File,
): Promise<DailyReportFileResult> {
  const id = crypto.randomUUID();
  try {
    if (isXlsbFile(file)) return await analyzeXlsbFile(file, id);
    const workbook = XLSX.read(await readFileArrayBuffer(file), {
      type: 'array',
    });
    return analyzeDailyReportWorkbook(workbook, file.name, id);
  } catch {
    return { status: 'unreadable', id, fileName: file.name };
  }
}
